import { useState } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import {
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { es } from 'date-fns/locale'
import { Sidebar, Dashboard, ProtectedRoute } from './components'
import { AuthProvider } from './auth'
import { LoginPage } from './components/auth/LoginPage'
import { useInitializeParametricData } from './hooks/useInitializeParametricData'
import ErrorBoundary from './components/common/ErrorBoundary'
import ErrorNotification from './components/common/ErrorNotification'

// Feature pages
import ArticulosPage from './features/catalogo/articulos/ArticulosPage'
import MarcasPage from './features/catalogo/marcas/MarcasPage'
import ProveedoresPage from './features/catalogo/proveedores/ProveedoresPage'
import AsociacionPage from './features/catalogo/asociacion/AsociacionPage'
import EspeciesPage from './features/catalogo/especies/EspeciesPage'
import DueniosPage from './features/catalogo/duenios/DueniosPage'
import EncargadosPage from './features/catalogo/encargados/EncargadosPage'
import DERCPage from './features/produccion/derc/DERCPage'
import GuiaMateriaPage from './features/produccion/guias-materia-prima/GuiaMateriaPage'
import GuiaMateriaFormPage from './features/produccion/guias-materia-prima/GuiaMateriaFormPage'
import GuiasProduccionPage from './features/produccion/guias-produccion/GuiasProduccionPage'
import GuiaProduccionFormPage from './features/produccion/guias-produccion/GuiaProduccionFormPage'
import ParteProduccionPage from './features/produccion/partes-produccion/ParteProduccionPage'
import ParteProduccionFormPage from './features/produccion/partes-produccion/ParteProduccionFormPage'
import ReportsPage from './features/reports/ReportsPage'
import SettingsPage from './features/settings/SettingsPage'
import UsuariosPage from './features/seguridad/usuarios/components/UsuariosPage'
import RolesPage from './features/seguridad/usuarios/components/RolesPage'
import PermisosPage from './features/seguridad/usuarios/components/PermisosPage'

// Planilla pages
import TrabajadoresPage from './features/planilla/trabajadores/components/TrabajadoresPage'
import ConceptosPage from './features/planilla/conceptos/components/ConceptosPage'
import PeriodosPage from './features/planilla/periodos/components/PeriodosPage'
import BasesCalculoPage from './features/planilla/formulas/components/BasesCalculoPage'
import PlanillonPage from './features/planilla/planillon/components/PlanillonPage'
import BeneficiosPage from './features/planilla/beneficios/components/BeneficiosPage'
import LiquidacionPage from './features/planilla/liquidacion/components/LiquidacionPage'
import DashboardPlanillaPage from './features/planilla/dashboard/components/DashboardPlanillaPage'
import PLAMEPage from './features/planilla/plame/components/PLAMEPage'
import AsistenciaPage from './features/planilla/asistencia/components/AsistenciaPage'
import UtilidadesPage from './features/planilla/utilidades/components/UtilidadesPage'
import ReportesPage from './features/planilla/reportes/components/ReportesPage'
import ConfiguracionPage from './features/planilla/configuracion/components/ConfiguracionPage'
import VariablesPage from './features/planilla/variables/components/VariablesPage'
import AsientosPage from './features/planilla/asientos/components/AsientosPage'
import TurnosPage from './features/planilla/turnos/components/TurnosPage'
import AfectacionesPage from './features/planilla/afectaciones/components/AfectacionesPage'
import TareoPage from './features/planilla/tareo/components/TareoPage'
import HonorariosPage from './features/planilla/honorarios/components/HonorariosPage'
import CamposAdicionalesPage from './features/planilla/campos-adicionales/components/CamposAdicionalesPage'
import DescansoMedicoPage from './features/planilla/descanso-medico/components/DescansoMedicoPage'
import DescuentosJudicialesPage from './features/planilla/descuentos-judiciales/components/DescuentosJudicialesPage'
import RendimientoPage from './features/planilla/rendimiento/components/RendimientoPage'
import RendimientoFormPage from './features/planilla/rendimiento/components/RendimientoFormPage'

// Tesoreria pages
import CajasBancosPage from './features/tesoreria/cajas-bancos/components/CajasBancosPage'
import MovimientosTesoreriaPage from './features/tesoreria/movimientos/components/MovimientosPage'
import MovimientoFormPage from './features/tesoreria/movimientos/components/MovimientoFormPage'
import ExploradorTesoreriaPage from './features/tesoreria/explorador/components/ExploradorTesoreriaPage'
import DisponibilidadBancariaPage from './features/tesoreria/disponibilidad/components/DisponibilidadBancariaPage'
import ConciliacionBancariaPage from './features/tesoreria/conciliacion/components/ConciliacionBancariaPage'
import CuentasCobrarPagarPage from './features/tesoreria/cuentas/components/CuentasCobrarPagarPage'
import FlujoCajaPage from './features/tesoreria/flujo-caja/components/FlujoCajaPage'

// Contabilidad pages
import PlanCuentasPage from './features/contabilidad/plan-cuentas/components/PlanCuentasPage'
import ConfiguracionContablePage from './features/contabilidad/configuracion/components/ConfiguracionContablePage'
import EjerciciosContablesPage from './features/contabilidad/ejercicios/components/EjerciciosPage'
import VouchersPage from './features/contabilidad/vouchers/components/VouchersPage'
import VoucherFormPage from './features/contabilidad/vouchers/components/VoucherFormPage'
import CentrosCostoPage from './features/contabilidad/centros-costo/components/CentrosCostoPage'
import ReportesContablesPage from './features/contabilidad/reportes/components/ReportesContablesPage'

// Inventario pages
import AlmacenesPage from './features/inventario/almacenes/components/AlmacenesPage'
import StockPage from './features/inventario/stock/components/StockPage'
import MovimientosInventarioPage from './features/inventario/movimientos/components/MovimientosPage'
import KardexPage from './features/inventario/kardex/components/KardexPage'
import LotesPage from './features/inventario/lotes/components/LotesPage'
import SerialesPage from './features/inventario/seriales/components/SerialesPage'
import TomaInventarioPage from './features/inventario/toma-inventario/components/TomaInventarioPage'
import GuiasEntradaPage from './features/inventario/guias-entrada/components/GuiasEntradaPage'
import GuiasSalidaPage from './features/inventario/guias-salida/components/GuiasSalidaPage'

// Ventas
import { ExploradorVentasPage } from './features/ventas/explorador/components/ExploradorVentasPage'
import { VentaFormPage } from './features/ventas/movimiento/components/VentaFormPage'
import { CotizacionesPage } from './features/ventas/cotizaciones/components/CotizacionesPage'
import { CotizacionFormPage } from './features/ventas/cotizaciones/components/CotizacionFormPage'
import { OrdenesPedidoPage } from './features/ventas/ordenes-pedido/components/OrdenesPedidoPage'
import { OrdenPedidoFormPage } from './features/ventas/ordenes-pedido/components/OrdenPedidoFormPage'
import { CobranzaPage } from './features/ventas/cobranza/components/CobranzaPage'
import { CobranzaFormPage } from './features/ventas/cobranza/components/CobranzaFormPage'
import { ListaPreciosPage } from './features/ventas/lista-precios/components/ListaPreciosPage'
import { ListaPrecioFormPage } from './features/ventas/lista-precios/components/ListaPrecioFormPage'
import { NotasCreditoPage } from './features/ventas/notas-credito/components/NotasCreditoPage'
import { NotaCreditoFormPage } from './features/ventas/notas-credito/components/NotaCreditoFormPage'
import { ComprobantesElectronicosPage } from './features/ventas/comprobantes-electronicos/components/ComprobantesElectronicosPage'
import { SeriesPage } from './features/pos/series/SeriesPage'
import { ComisionesPage } from './features/ventas/comisiones/components/ComisionesPage'
import { ReportesVentasPage } from './features/ventas/reportes/components/ReportesVentasPage'

// Compras
import { SolicitudesRequerimientoPage } from './features/compras/solicitud-requerimiento/components/SolicitudesRequerimientoPage'
import { SolicitudRequerimientoFormPage } from './features/compras/solicitud-requerimiento/components/SolicitudRequerimientoFormPage'
import { SolicitudesCompraPage } from './features/compras/solicitud-compra/components/SolicitudesCompraPage'
import { SolicitudCompraFormPage } from './features/compras/solicitud-compra/components/SolicitudCompraFormPage'
import { OrdenesCompraPage } from './features/compras/orden-compra/components/OrdenesCompraPage'
import { OrdenCompraFormPage } from './features/compras/orden-compra/components/OrdenCompraFormPage'
import { FacturasCompraPage } from './features/compras/factura-compra/components/FacturasCompraPage'
import { FacturaCompraFormPage } from './features/compras/factura-compra/components/FacturaCompraFormPage'
import { GuiasEntradaCompraPage } from './features/compras/guia-entrada/components/GuiasEntradaCompraPage'
import { GuiaEntradaCompraFormPage } from './features/compras/guia-entrada/components/GuiaEntradaCompraFormPage'

// POS
import { PosLayout } from './features/pos/components/PosLayout'
import { PosRetailPage } from './features/pos/components/PosRetailPage'
import { PosRestaurantePage } from './features/pos/components/PosRestaurantePage'
import { KitchenOrderView } from './features/pos/components/KitchenOrderView'

const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#3b82f6',
    },
    secondary: {
      main: '#10b981',
    },
    background: {
      default: mode === 'light' ? '#f9fafb' : '#111827',
      paper: mode === 'light' ? '#ffffff' : '#1f2937',
    },
    text: {
      primary: mode === 'light' ? '#1e293b' : '#f1f5f9',
      secondary: mode === 'light' ? '#64748b' : '#94a3b8',
    },
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, system-ui, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: mode === 'light' ? '#f9fafb' : '#111827',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
          color: mode === 'light' ? '#1e293b' : '#f1f5f9',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#ffffff' : '#1f2937',
          color: mode === 'light' ? '#1e293b' : '#f1f5f9',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

const AppContent: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()

  // Initialize parametric data
  useInitializeParametricData()

  const theme = createAppTheme(darkMode ? 'dark' : 'light')

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleThemeToggle = () => {
    setDarkMode(!darkMode)
  }

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary
        resetOnPropsChange
        resetKeys={[location.pathname]}
      >
        {/* Mobile hamburger menu button */}
        <Box
          component="button"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            zIndex: 1300,
            width: 40,
            height: 40,
            borderRadius: 2,
            border: 'none',
            backgroundColor: '#1e293b',
            color: 'white',
            fontSize: 24,
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          &#9776;
        </Box>

        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
          <Sidebar
            mobileOpen={mobileOpen}
            onMobileToggle={handleDrawerToggle}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
            darkMode={darkMode}
            onThemeToggle={handleThemeToggle}
          />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: '100vh',
              backgroundColor: '#f9fafb',
              overflowX: 'hidden',
              pt: { xs: '48px', md: 0 },
            }}
          >
            <ErrorBoundary resetOnPropsChange resetKeys={[location.pathname]}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/catalogo/articulos" element={<ArticulosPage />} />
                <Route path="/catalogo/marcas" element={<MarcasPage />} />
                <Route path="/catalogo/proveedores" element={<ProveedoresPage />} />
                <Route path="/catalogo/asociacion" element={<AsociacionPage />} />
                <Route path="/catalogo/especies" element={<EspeciesPage />} />
                <Route path="/catalogo/duenios" element={<DueniosPage />} />
                <Route path="/catalogo/encargados" element={<EncargadosPage />} />
                <Route path="/produccion/derc" element={<DERCPage />} />
                <Route path="/produccion/guias-materia-prima" element={<GuiaMateriaPage />} />
                <Route path="/produccion/guias-materia-prima/:id" element={<GuiaMateriaFormPage />} />
                <Route path="/produccion/guias-produccion" element={<GuiasProduccionPage />} />
                <Route path="/produccion/guias-produccion/:id" element={<GuiaProduccionFormPage />} />
                <Route path="/produccion/partes-produccion" element={<ParteProduccionPage />} />
                <Route path="/produccion/partes-produccion/:id" element={<ParteProduccionFormPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/seguridad/usuarios" element={<UsuariosPage />} />
                <Route path="/seguridad/roles" element={<RolesPage />} />
                <Route path="/seguridad/permisos" element={<PermisosPage />} />
                {/* Planilla */}
                <Route path="/planilla/trabajadores" element={<TrabajadoresPage />} />
                <Route path="/planilla/conceptos" element={<ConceptosPage />} />
                <Route path="/planilla/periodos" element={<PeriodosPage />} />
                <Route path="/planilla/bases-calculo" element={<BasesCalculoPage />} />
                <Route path="/planilla/planillon" element={<PlanillonPage />} />
                <Route path="/planilla/beneficios" element={<BeneficiosPage />} />
                <Route path="/planilla/liquidacion" element={<LiquidacionPage />} />
                <Route path="/planilla/dashboard" element={<DashboardPlanillaPage />} />
                <Route path="/planilla/plame" element={<PLAMEPage />} />
                <Route path="/planilla/asistencia" element={<AsistenciaPage />} />
                <Route path="/planilla/descanso-medico" element={<DescansoMedicoPage />} />
                <Route path="/planilla/rendimiento" element={<RendimientoPage />} />
                <Route path="/planilla/rendimiento/nuevo" element={<RendimientoFormPage />} />
                <Route path="/planilla/rendimiento/:id" element={<RendimientoFormPage />} />
                <Route path="/planilla/descuentos-judiciales" element={<DescuentosJudicialesPage />} />
                <Route path="/planilla/utilidades" element={<UtilidadesPage />} />
                <Route path="/planilla/reportes" element={<ReportesPage />} />
                <Route path="/planilla/configuracion" element={<ConfiguracionPage />} />
                <Route path="/planilla/variables" element={<VariablesPage />} />
                <Route path="/planilla/asientos" element={<AsientosPage />} />
                <Route path="/planilla/turnos" element={<TurnosPage />} />
                <Route path="/planilla/afectaciones" element={<AfectacionesPage />} />
                <Route path="/planilla/tareo" element={<TareoPage />} />
                <Route path="/planilla/honorarios" element={<HonorariosPage />} />
                <Route path="/planilla/campos-adicionales" element={<CamposAdicionalesPage />} />
                {/* Contabilidad */}
                <Route path="/contabilidad/plan-cuentas" element={<PlanCuentasPage />} />
                <Route path="/contabilidad/configuracion" element={<ConfiguracionContablePage />} />
                <Route path="/contabilidad/ejercicios" element={<EjerciciosContablesPage />} />
                <Route path="/contabilidad/vouchers" element={<VouchersPage />} />
                <Route path="/contabilidad/vouchers/nuevo" element={<VoucherFormPage />} />
                <Route path="/contabilidad/vouchers/:id" element={<VoucherFormPage />} />
                <Route path="/contabilidad/centros-costo" element={<CentrosCostoPage />} />
                <Route path="/contabilidad/reportes" element={<ReportesContablesPage />} />
                {/* Tesoreria */}
                <Route path="/tesoreria/cajas-bancos" element={<CajasBancosPage />} />
                <Route path="/tesoreria/movimientos" element={<MovimientosTesoreriaPage />} />
                <Route path="/tesoreria/movimientos/nuevo/:tipo" element={<MovimientoFormPage />} />
                <Route path="/tesoreria/movimientos/:id" element={<MovimientoFormPage />} />
                <Route path="/tesoreria/explorador" element={<ExploradorTesoreriaPage />} />
                <Route path="/tesoreria/disponibilidad-bancaria" element={<DisponibilidadBancariaPage />} />
                <Route path="/tesoreria/conciliacion-bancaria" element={<ConciliacionBancariaPage />} />
                <Route path="/tesoreria/cuentas-cobrar-pagar" element={<CuentasCobrarPagarPage />} />
                <Route path="/tesoreria/flujo-caja" element={<FlujoCajaPage />} />
                {/* Inventario */}
                <Route path="/inventario/almacenes" element={<AlmacenesPage />} />
                <Route path="/inventario/stock" element={<StockPage />} />
                <Route path="/inventario/movimientos" element={<MovimientosInventarioPage />} />
                <Route path="/inventario/kardex" element={<KardexPage />} />
                <Route path="/inventario/lotes" element={<LotesPage />} />
                <Route path="/inventario/seriales" element={<SerialesPage />} />
                <Route path="/inventario/toma-inventario" element={<TomaInventarioPage />} />
                <Route path="/inventario/guias-entrada" element={<GuiasEntradaPage />} />
                <Route path="/inventario/guias-salida" element={<GuiasSalidaPage />} />
                {/* Ventas */}
                <Route path="/ventas/explorador" element={<ExploradorVentasPage />} />
                <Route path="/ventas/nueva" element={<VentaFormPage />} />
                <Route path="/ventas/:id" element={<VentaFormPage />} />
                <Route path="/ventas/cotizaciones" element={<CotizacionesPage />} />
                <Route path="/ventas/cotizaciones/nueva" element={<CotizacionFormPage />} />
                <Route path="/ventas/cotizaciones/:id" element={<CotizacionFormPage />} />
                <Route path="/ventas/ordenes-pedido" element={<OrdenesPedidoPage />} />
                <Route path="/ventas/ordenes-pedido/nueva" element={<OrdenPedidoFormPage />} />
                <Route path="/ventas/ordenes-pedido/:id" element={<OrdenPedidoFormPage />} />
                <Route path="/ventas/cobranza" element={<CobranzaPage />} />
                <Route path="/ventas/cobranza/nueva" element={<CobranzaFormPage />} />
                <Route path="/ventas/cobranza/:id" element={<CobranzaFormPage />} />
                <Route path="/ventas/lista-precios" element={<ListaPreciosPage />} />
                <Route path="/ventas/lista-precios/nueva" element={<ListaPrecioFormPage />} />
                <Route path="/ventas/lista-precios/:id" element={<ListaPrecioFormPage />} />
                <Route path="/ventas/notas-credito" element={<NotasCreditoPage />} />
                <Route path="/ventas/notas-credito/nueva" element={<NotaCreditoFormPage />} />
                <Route path="/ventas/notas-credito/:id" element={<NotaCreditoFormPage />} />
                <Route path="/ventas/comprobantes-electronicos" element={<ComprobantesElectronicosPage />} />
                <Route path="/ventas/comisiones" element={<ComisionesPage />} />
                <Route path="/ventas/reportes" element={<ReportesVentasPage />} />
                <Route path="/ventas/series" element={<SeriesPage />} />

                {/* Compras */}
                <Route path="/compras/solicitud-requerimiento" element={<SolicitudesRequerimientoPage />} />
                <Route path="/compras/solicitud-requerimiento/nueva" element={<SolicitudRequerimientoFormPage />} />
                <Route path="/compras/solicitud-requerimiento/:id" element={<SolicitudRequerimientoFormPage />} />
                <Route path="/compras/solicitud-compra" element={<SolicitudesCompraPage />} />
                <Route path="/compras/solicitud-compra/nueva" element={<SolicitudCompraFormPage />} />
                <Route path="/compras/solicitud-compra/:id" element={<SolicitudCompraFormPage />} />
                <Route path="/compras/orden-compra" element={<OrdenesCompraPage />} />
                <Route path="/compras/orden-compra/nueva" element={<OrdenCompraFormPage />} />
                <Route path="/compras/orden-compra/:id" element={<OrdenCompraFormPage />} />
                <Route path="/compras/factura-compra" element={<FacturasCompraPage />} />
                <Route path="/compras/factura-compra/nueva" element={<FacturaCompraFormPage />} />
                <Route path="/compras/factura-compra/:id" element={<FacturaCompraFormPage />} />
                <Route path="/compras/guia-entrada" element={<GuiasEntradaCompraPage />} />
                <Route path="/compras/guia-entrada/nueva" element={<GuiaEntradaCompraFormPage />} />
                <Route path="/compras/guia-entrada/:id" element={<GuiaEntradaCompraFormPage />} />
              </Routes>
            </ErrorBoundary>
          </Box>
        </Box>
        <ErrorNotification />
      </ErrorBoundary>
    </ThemeProvider>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <AuthProvider>
        <Router>
          <ErrorBoundary resetOnPropsChange>
            <Routes>
              <Route path="/login" element={
                <ErrorBoundary>
                  <LoginPage />
                </ErrorBoundary>
              } />
              <Route path="/pos" element={
                <ProtectedRoute>
                  <PosLayout />
                </ProtectedRoute>
              }>
                <Route index element={<PosRetailPage />} />
                <Route path="retail" element={<PosRetailPage />} />
                <Route path="restaurante" element={<PosRestaurantePage />} />
                <Route path="cocina" element={<KitchenOrderView />} />
              </Route>
              <Route path="/*" element={
                <ProtectedRoute>
                  <AppContent />
                </ProtectedRoute>
              } />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
      </LocalizationProvider>
    </ErrorBoundary>
  )
}

export default App
